import React, { useState, useRef, useCallback } from 'react'
import glamorous from 'glamorous'
// import clientData from 'utils/client-data'
import { isEqual, debounce } from 'lodash'
import { css } from 'glamor'
import { HeadingText, BodyText, TextInput } from '@paypalcorp/pp-react'
import getContent from 'pp-react-l10n'
import { showServiceError } from './../../error/util'
import SaveButton from '../shared/save-button'

const l10n = getContent('pages/seller/settings')
const l10nSocial = getContent('components/social_media_link')

const Container = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
})

const Heading = css({
  marginTop: 80,
  '@media(max-width: 414px)': {
    marginTop: 20,
  },
})

const SubHeading = css({
  textAlign: 'left',
  margin: '16px 0 20px',
})

const FormInput = css({
  marginTop: 24,
})

const initializeSocialsData = (
  instagramValue,
  facebookValue,
  twitterValue,
  websiteValue,
) => {
  return {
    instagram: {
      ariaLabel: 'instagramRadio',
      ariaLabelDone: 'instagramRadioDone',
      label: 'instagramInputLabel',
      validation: /^[A-Za-z0-9_.]{1,30}$/,
      error: 'userNameInputInputError',
      showError: false,
      visibleInput: false,
      doneState: false,
      path: '/user_info/instagram_username',
      value: instagramValue === '-' ? '' : instagramValue,
    },
    facebook: {
      ariaLabel: 'facebookRadio',
      ariaLabelDone: 'facebookRadioDone',
      label: 'facebookInputLabel',
      validation: /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?(facebook\.|fb\.).+\/.+/i,
      error: 'fbInputInputError',
      showError: false,
      visibleInput: false,
      doneState: false,
      path: '/user_info/facebook_url',
      value: facebookValue === '-' ? '' : facebookValue,
    },
    twitter: {
      ariaLabel: 'twitterRadio',
      ariaLabelDone: 'twitterRadioDone',
      label: 'twitterInputLabel',
      validation: /^[A-Za-z0-9_]{4,15}$/,
      error: 'userNameInputInputError',
      showError: false,
      visibleInput: false,
      doneState: false,
      path: '/user_info/twitter_handle',
      value: twitterValue === '-' ? '' : twitterValue,
    },
    website: {
      ariaLabel: 'websiteRadio',
      ariaLabelDone: 'websiteRadioDone',
      label: 'websiteInputLabel',
      validation: /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i,
      error: 'urlInputInputError',
      showError: false,
      visibleInput: false,
      doneState: false,
      path: '/user_info/website',
      value: websiteValue === '-' ? '' : websiteValue,
    },
  }
}

const initialValidation = (socialsData) => {
  Object.keys(socialsData).forEach((socialName) => {
    const value = socialsData[socialName].value
    if (value !== '' && !socialsData[socialName].validation.test(value)) {
      socialsData[socialName] = {
        ...socialsData[socialName],
        showError: true,
        doneState: false,
      }
    } else if (value !== '') {
      socialsData[socialName] = {
        ...socialsData[socialName],
        showError: false,
        doneState: true,
      }
    } else {
      socialsData[socialName] = {
        ...socialsData[socialName],
        showError: false,
        doneState: false,
      }
    }
  })
  if (checkFormValid(socialsData)) {
    return true
  }
  return false
}

// Check if form is valid
const checkFormValid = (data) => {
  const validatedButtons = []
  const errorButtons = []
  Object.keys(data).map((socialName) => {
    validatedButtons.push(data[socialName].doneState)
    errorButtons.push(data[socialName].showError)
  })
  if (validatedButtons.includes(true) && !errorButtons.includes(true)) {
    return true
  }
  return false
}

function SocialSetting(props) {
  const {
    sellerProfileState: {
      state: {
        sellerProfileDetails: {
          id: businessHandle = '',
          user_info: {
            instagram_username: instagramValue = '',
            facebook_url: facebookValue = '',
            twitter_handle: twitterValue = '',
            website: websiteValue = '',
          } = {},
        } = {},
      } = {},
    } = {},
  } = props
  const [socialsData, setSocialsData] = useState(
    initializeSocialsData(
      instagramValue,
      facebookValue,
      twitterValue,
      websiteValue,
    ),
  )
  const [isFormValid, setIsFormValid] = useState(initialValidation(socialsData))
  const { editSellerSettings } = props.sellerProfileState

  const formValues = []
  Object.keys(socialsData).map((socialName) => {
    formValues.push(socialsData[socialName].value)
  })
  const { current: initialValues } = useRef(formValues)
  const [isChanged, setIsChanged] = useState(
    !isEqual(initialValues, formValues),
  )
  const delayedValidation = useCallback(
    debounce((fn, ...args) => fn(...args), 1000),
    [],
  )

  const handleInputTyping = async (e) => {
    e.persist()
    const inputValue = e.target.value
    const socialName = e.target.id.substr(6)
    setSocialsData((prevState) => ({
      ...prevState,
      [socialName]: {
        ...prevState[socialName],
        value: inputValue,
      },
    }))
    // on tab out validate right away
    if (e.type === 'blur') {
      validateInput(e)
    } else {
      // when typing validate with a delay
      await delayedValidation(validateInput, e)
    }
    setIsChanged(!isEqual(initialValues, formValues))
  }

  /**
   * This helper method validates input field values
   * @param {Event} e - change event object
   */
  const validateInput = (e) => {
    const inputValue = e.target.value
    const socialName = e.target.id.substr(6)
    // if validation doesn't pass show the error text
    if (
      inputValue !== '' &&
      !socialsData[socialName].validation.test(inputValue)
    ) {
      setSocialsData((prevState) => ({
        ...prevState,
        [socialName]: {
          ...prevState[socialName],
          showError: true,
          doneState: false,
        },
      }))
    } else if (inputValue !== '') {
      setSocialsData((prevState) => ({
        ...prevState,
        [socialName]: {
          ...prevState[socialName],
          showError: false,
          doneState: true,
        },
      }))
    } else {
      setSocialsData((prevState) => ({
        ...prevState,
        [socialName]: {
          ...prevState[socialName],
          showError: false,
          doneState: false,
        },
      }))
    }

    setIsFormValid(checkFormValid(socialsData))
  }

  const handleSave = async () => {
    const valueObj = []
    Object.keys(socialsData).forEach((socialName) => {
      const field = socialsData[socialName]
      if (field.value) {
        valueObj.push({
          op: 'replace',
          path: field.path,
          value: field.value,
        })
      }
    })

    try {
      await editSellerSettings(valueObj, businessHandle)
      props.history.push(`/seller/${businessHandle}/settings`)
    } catch (err) {
      showServiceError(err, props.history)
    }
  }

  return (
    <Container>
      <HeadingText
        size="md"
        role="heading"
        aria-level="1"
        className={`${Heading}`}
      >
        {l10n('socialMedia')}
      </HeadingText>
      <BodyText className={`${SubHeading}`}>
        {l10nSocial('headerText')}
      </BodyText>
      {Object.keys(socialsData).map((socialName) => (
        <TextInput
          key={socialName}
          maxLength="350"
          id={`input-${socialName}`}
          value={socialsData[socialName].value}
          onChange={handleInputTyping}
          onBlur={handleInputTyping}
          className={`${FormInput}`}
          label={l10nSocial(socialsData[socialName].label)}
          errorText={
            socialsData[socialName].showError &&
            l10nSocial(socialsData[socialName].error)
          }
        />
      ))}
      {isChanged && isFormValid && <SaveButton handleSave={handleSave} />}
    </Container>
  )
}

export default SocialSetting
